import { Component, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgbDropdown, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom, Subscription } from 'rxjs';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { UploadJobStatusResponse } from 'src/app/modules/rate-manager/components/bulk-upload-preview/bulk-upload-preview.types';
import { BulkUploadProgressService } from 'src/app/modules/rate-manager/components/bulk-create-rate-modal/bulk-upload-progress.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';

/**
 * Terminal statuses that stop polling.
 * Any status NOT in this set is considered "in progress" and keeps the poll alive.
 */
const TERMINAL_STATUSES = new Set([
  'preview_ready',
  'committed',
  'failed',
  'commit_failed',
  'validation_failed',
  'validated',
  'cancelled',
]);

export type JobTab = 'in-progress' | 'list';

export interface JobItem {
  id: string;
  status: string;
  progress: number;
  stage: string;
  message: string;
  totalRows: number | null;
  processingPath: string;
  fileName: string;
  fileSize: number | null;
  /** Display-friendly category label from the API (e.g. "Cars") */
  categoryDisplayName: string;
  createdAt?: string;
  /** Full raw API response */
  raw?: UploadJobStatusResponse;
}

@Component({
  selector: 'app-job-status-bar',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule],
  templateUrl: './job-status-bar.component.html',
  styleUrl: './job-status-bar.component.scss',
})
export class JobStatusBarComponent implements OnInit, OnDestroy {
  @ViewChild('jobDropdown') jobDropdown!: NgbDropdown;

  activeTab: JobTab = 'in-progress';

  /** Current/latest job from GET /upload-jobs */
  currentJob: JobItem | null = null;

  /** History list from GET /upload-jobs/list */
  jobList: JobItem[] = [];
  listLoading = false;
  listLoaded = false;

  /** Polling timer — only active when currentJob is in a non-terminal status */
  private pollTimer?: ReturnType<typeof setInterval>;

  /** Subscription to job-state-change notifications (commit/upload/failure). */
  private jobsChangedSub?: Subscription;

  private rateManagerService = inject(RateManagerService);
  private uploadProgress = inject(BulkUploadProgressService);
  private router = inject(Router);

  private readonly POLL_INTERVAL_MS = 4000;

  ngOnInit(): void {
    this.fetchLatestJob();

    // When a job reaches a terminal state (e.g. commit succeeds), refresh
    // both the in-progress job and the history list so they show the
    // latest status without waiting for the next poll.
    this.jobsChangedSub = this.uploadProgress.jobsChanged.subscribe(() => {
      this.refreshJobs();
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
    this.jobsChangedSub?.unsubscribe();
  }

  /** Refresh the current job and, if it has been loaded, the history list. */
  private refreshJobs(): void {
    this.fetchLatestJob();
    // Only refetch the list if it was previously loaded so we keep it in sync.
    if (this.listLoaded) {
      this.fetchJobList();
    }
  }

  // ─── Data fetching ──────────────────────────────────────────────────────────

  private async fetchLatestJob(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.rateManagerService.getLatestUploadJob(),
      );
      const raw: UploadJobStatusResponse = response?.data || response;
      if (raw && (raw.job_id || raw.id)) {
        this.currentJob = this.mapJob(raw);
        this.handlePollingDecision();
      } else {
        this.currentJob = null;
        this.stopPolling();
      }
    } catch {
      // Silently ignore
    }
  }

  private async fetchJobList(): Promise<void> {
    this.listLoading = true;
    try {
      const response = await firstValueFrom(
        this.rateManagerService.getUploadJobsList(),
      );
      // API returns { data: { jobs: [...], total, page, page_size } }
      const rawList: UploadJobStatusResponse[] =
        response?.data?.jobs || response?.data || [];
      this.jobList = (Array.isArray(rawList) ? rawList : []).map((j) =>
        this.mapJob(j),
      );
      this.listLoaded = true;
    } catch {
      // Silently ignore
    } finally {
      this.listLoading = false;
    }
  }

  private handlePollingDecision(): void {
    if (this.currentJob && !TERMINAL_STATUSES.has(this.currentJob.status)) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  private startPolling(): void {
    if (this.pollTimer) return;
    this.pollTimer = setInterval(
      () => this.fetchLatestJob(),
      this.POLL_INTERVAL_MS,
    );
  }

  private stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  private mapJob(raw: UploadJobStatusResponse): JobItem {
    const id = String(raw.job_id || raw.id);
    return {
      id,
      status: raw.status || 'unknown',
      progress: raw.progress_percent ?? raw.progress ?? 0,
      stage: raw.stage || raw.status || '',
      message: raw.message || raw.error_message || '',
      totalRows: raw.total_rows ?? null,
      processingPath: raw.processing_path || '',
      fileName: raw.original_filename || raw.file_name || '',
      fileSize: raw.file_size ?? null,
      categoryDisplayName:
        raw.category_display_name || raw.rate_category_name || '',
      createdAt: raw.created_at,
      raw,
    };
  }

  // ─── Actions ────────────────────────────────────────────────────────────────

  setTab(tab: JobTab): void {
    this.activeTab = tab;
    if (tab === 'list') {
      this.fetchJobList();
    }
  }

  onDropdownToggle(open: boolean): void {
    if (open) {
      this.activeTab = 'in-progress';
      this.fetchLatestJob();
    }
  }

  viewPreview(job: JobItem): void {
    this.jobDropdown?.close();
    this.router.navigate(['/instant-rate-manager/bulk-upload-preview'], {
      queryParams: { jobId: job.id },
    });
  }

  async cancelJob(job: JobItem, event: MouseEvent): Promise<void> {
    event.stopPropagation();
    const result = await AlertUtil.confirm(
      'Cancel Job',
      `Are you sure you want to cancel this job?`,
      'Yes, cancel it',
      'No, keep it',
    );
    if (!result.isConfirmed) return;

    try {
      await firstValueFrom(this.rateManagerService.deleteUploadJob(job.id));
    } catch {
      /* ignore */
    }
    this.jobDropdown?.close();
    this.currentJob = null;
    this.stopPolling();
    this.fetchLatestJob();
  }

  async deleteJob(job: JobItem, event: MouseEvent): Promise<void> {
    event.stopPropagation();
    const result = await AlertUtil.confirm(
      'Delete Job',
      `Are you sure you want to delete this job?`,
      'Yes, delete it',
      'No, keep it',
    );
    if (!result.isConfirmed) return;

    try {
      await firstValueFrom(this.rateManagerService.deleteUploadJob(job.id));
    } catch {
      /* ignore */
    }
    this.jobDropdown?.close();
    this.jobList = this.jobList.filter((j) => j.id !== job.id);
  }

  // ─── UI helpers ─────────────────────────────────────────────────────────────

  get inProgressCount(): number {
    return this.currentJob && !TERMINAL_STATUSES.has(this.currentJob.status)
      ? 1
      : 0;
  }

  get isLatestJobPreviewReady(): boolean {
    return this.currentJob?.status === 'preview_ready';
  }

  isInProgress(status: string): boolean {
    return !TERMINAL_STATUSES.has(status);
  }

  isPreviewReady(status: string): boolean {
    return status === 'preview_ready';
  }

  isCompleted(status: string): boolean {
    return status === 'committed';
  }

  isFailed(status: string): boolean {
    return (
      status === 'failed' ||
      status === 'validation_failed' ||
      status === 'commit_failed'
    );
  }

  isCancelled(status: string): boolean {
    return status === 'cancelled';
  }

  getStatusBadgeClass(status: string): string {
    if (this.isCancelled(status)) return 'badge-cancelled';
    if (this.isInProgress(status)) return 'badge-in-progress';
    if (this.isPreviewReady(status)) return 'badge-preview-ready';
    if (this.isCompleted(status)) return 'badge-completed';
    if (this.isFailed(status)) return 'badge-failed';
    return 'badge-default';
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'queued':
        return 'Queued';
      case 'validating_template':
        return 'Validating Template';
      case 'ai_converting':
        return 'AI Converting';
      case 'validating_output':
        return 'Validating Output';
      case 'in_progress':
        return 'In Progress';
      case 'preview_ready':
        return 'Preview Ready';
      case 'committing':
        return 'Committing';
      case 'committed':
        return 'Committed';
      case 'cancelled':
        return 'Cancelled';
      case 'failed':
        return 'Failed';
      case 'commit_failed':
        return 'Commit Failed';
      case 'validation_failed':
        return 'Validation Failed';
      default:
        return status;
    }
  }

  trackByJobId(_index: number, job: JobItem): string {
    return job.id;
  }
}
